import { useDeckBinding } from './DeckDataContext';
import React, { useCallback, useRef, useState } from 'react';
import { View, ScrollView, StyleSheet } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';

import useRequireAuth from '@/hooks/useRequireAuth';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';

export default function DeckMockup() {
  const { store: useDeckStore, routePath } = useDeckBinding();
  const { colors } = useTheme();
  const router = useRouter();
  const requireAuth = useRequireAuth();
  const createDeck = useDeckStore(state => state.actions.createDeck);
  const decksLength = useDeckStore(state => state.decks.length);
  const maxDecks = useDeckStore(state => state.limits.maxDecks);
  const pendingRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const create = useCallback(async () => {
    if (pendingRef.current || decksLength >= maxDecks) return;
    const authenticated = requireAuth(() => true, {
      source: 'deck_start_blank', title: 'Sign in to create a deck',
      subtitle: 'Save your conversations together in a workspace that follows you across devices.', icon: 'table-columns',
    });
    if (authenticated !== true) return;
    pendingRef.current = true;
    setBusy(true);
    try {
      const slug = await createDeck({ name: 'Untitled deck', columns: [
        { type: 'conversation', ref: { kind: 'Conversation', id: null } },
        { type: 'conversation', ref: { kind: 'Conversation', id: null } },
      ] });
      if (slug) router.push({ pathname: routePath, params: { d: slug } });
    } finally {
      pendingRef.current = false;
      setBusy(false);
    }
  }, [createDeck, decksLength, maxDecks, requireAuth, routePath, router]);
  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.solid_color_primary }} contentContainerStyle={styles.canvas}>
      <View style={styles.content}>
        <View style={[styles.symbol, { backgroundColor: colors.solid_color_secondary }]}>
          <Icon name="table-columns" size={28} color={colors.text_color_primary} />
        </View>
        <Text size="xxl" weight="semibold" style={styles.title}>A space to see more.</Text>
        <Text size="n" color="secondary" style={styles.copy}>
          Bring conversations, models, and tools together. Give each idea its own column, and keep everything in view.
        </Text>
        <View style={styles.actions}>
          <Button text={busy ? 'Creating deck…' : 'Create a deck'} variant="primary" prependIcon="plus"
            onPress={create} isDisabled={busy || decksLength >= maxDecks} />
          <Button text="Browse recipes" variant="transparent" appendIcon="arrow-right" onPress={() => router.push('/decks/recipes')} />
        </View>
        <View style={styles.features}>
          {[
            ['message', 'Your chats, together', 'Start fresh or pick up any conversation.'],
            ['cube', 'A different perspective', 'Compare models and specialized bots.'],
            ['sliders', 'Make it your own', 'Resize, arrange, and save your workspace.'],
          ].map(([icon, title, copy]) => <View key={title} style={styles.feature}>
            <Icon name={icon} size={20} color={colors.text_color_secondary} />
            <Text size="s" weight="semibold" style={{ marginTop: 16 }}>{title}</Text>
            <Text size="s" color="secondary" style={styles.featureCopy}>{copy}</Text>
          </View>)}
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  canvas: { flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: 32 },
  content: { width: '100%', maxWidth: 760, alignItems: 'center', paddingVertical: 48 },
  symbol: { width: 72, height: 72, borderRadius: 20, alignItems: 'center', justifyContent: 'center', marginBottom: 24 },
  title: { textAlign: 'center', letterSpacing: -0.5 },
  copy: { maxWidth: 480, textAlign: 'center', lineHeight: 26, marginTop: 16 },
  actions: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 12, marginTop: 28 },
  features: { width: '100%', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 32, marginTop: 72 },
  feature: { flexBasis: 200, flexGrow: 1, maxWidth: 232 },
  featureCopy: { lineHeight: 22, marginTop: 8 },
});
